import { CommonModule, Location } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { ComponentCanDeactivate } from '../../guards/unsaved-changes.guard';
import { MatterClientDto, MatterDetailsDto } from '../../models/matter.models';
import { NotesWithMentionsDto } from '../../models/notes.models';
import { MatterAssignmentDto } from '../../models/request.models';
import { ResourceDto } from '../../models/resource.models';
import { AssignmentService } from '../../services/assignment.service';
import { AuthService } from '../../services/auth.service';
import {
  Breadcrumb,
  BreadcrumbService,
} from '../../services/breadcrumb.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { FloatingDialogService } from '../../services/floating-dialog.service';
import { MatterService } from '../../services/matter.service';
import { ResourceService } from '../../services/resource.service';
import { SnackbarService } from '../../services/snackbar.service';
import { AssignmentNotesPanelComponent } from '../shared/assignment-notes-panel/assignment-notes-panel.component';
import {
  AssignmentRow,
  AssignmentsCardComponent,
} from '../shared/assignments-card/assignments-card.component';
import { BreadcrumbComponent } from '../shared/breadcrumb/breadcrumb.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../shared/confirmation-dialog/confirmation-dialog.component';
import { EntityNotesComponent } from '../shared/entity-notes/entity-notes.component';
import { ErrorStateComponent } from '../shared/error-state/error-state.component';
import { CopyableTextComponent } from '../shared/copyable-text/copyable-text.component';
import { MatterBillingHistoryComponent } from '../shared/matter-billing-history/matter-billing-history.component';
import { describeApiError } from '../../utils/api-error.utils';
import {
  applyAssignmentRowEdits,
  toAssignmentRow,
} from '../../utils/assignment-row.utils';

@Component({
  selector: 'app-matter-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    RouterModule,
    ErrorStateComponent,
    MatterBillingHistoryComponent,
    EntityNotesComponent,
    AssignmentNotesPanelComponent,
    AssignmentsCardComponent,
    BreadcrumbComponent,
    CopyableTextComponent,
  ],
  templateUrl: './matter-details.component.html',
  styleUrls: ['./matter-details.component.scss'],
})
export class MatterDetailsComponent
  implements OnInit, OnDestroy, ComponentCanDeactivate
{
  /**
   * Client-matter number. Set by Angular from the @Input when the
   * component is rendered in a floating dialog, or assigned from the
   * route params when rendered as a full page (see ngOnInit).
   */
  @Input() clientMatterNumber: string | null = null;

  /**
   * Auto-injected by FloatingDialogService when the component is loaded
   * into a floating dialog. Its presence is the signal that we're in
   * dialog mode (used by `isDialogMode`).
   */
  @Input() floatingDialogId?: string;

  get isDialogMode(): boolean {
    return !!this.floatingDialogId;
  }

  /**
   * Maps a floating-dialog instance's inputs to the full-page route for
   * this component. Read by FloatingDialogContainerComponent to render
   * the "Open in main window" button in the dialog header.
   */
  static getMainWindowRoute(
    inputs: Record<string, unknown>,
  ): unknown[] | null {
    const id = inputs?.['clientMatterNumber'];
    return typeof id === 'string' && id ? ['/matters', id] : null;
  }

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private matterService = inject(MatterService);
  private assignmentService = inject(AssignmentService);
  private resourceService = inject(ResourceService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private breadcrumbService = inject(BreadcrumbService);
  private featureFlagService = inject(FeatureFlagService);
  private floatingDialogService = inject(FloatingDialogService);

  readonly entityNotes = viewChild(EntityNotesComponent);

  readonly assignmentNotesSection = viewChild('assignmentNotesSection', {
    read: ElementRef,
  });

  matter: MatterDetailsDto | null = null;
  isLoading = false;
  error: string | null = null;
  isNotesReadOnly: boolean = true;
  isComparisonPopUpsEnabled = false;
  private destroy$ = new Subject<void>();

  // Assignments
  assignments: MatterAssignmentDto[] = [];
  assignmentRows: AssignmentRow[] = [];
  isLoadingAssignments = false;
  /** The last assignments fetch failed; the Assignment Notes card shows its error. */
  assignmentsLoadFailed = false;
  /**
   * A silent refresh is in flight. A note edit made meanwhile is re-sent as a
   * fresh refresh (see onAssignmentChanged), so the older response cannot land
   * over it.
   */
  private silentRefreshPending = false;
  /**
   * Emits the client-matter number to load assignments for, and whether the load is silent
   * (see refreshAssignmentNotesSilently). Every load flows through the one
   * switchMap pipeline set up in the constructor, so only the latest request can
   * settle the list: reloads can overlap (a "Mark Active" refresh during an add or
   * remove), and the last response to arrive would otherwise win, stale or not.
   */
  private readonly assignmentsLoad$ = new Subject<{
    id: string;
    silent: boolean;
  }>();

  // Add-resource typeahead (direct assignment)
  canCreateAssignments = false;
  canDeleteAssignments = false;
  canEditAssignments = false;
  canReadResourceAssignmentNotes = false;
  canReadManagerAssignmentNotes = false;
  canWriteManagerAssignmentNotes = false;
  addResourceQuery = '';
  resourceSuggestions: ResourceDto[] = [];
  isSearchingResources = false;
  resourceNotFound = false;
  isCreatingAssignment = false;
  /** Whether the inline add flow is expanded (false shows the "+ Add Assignment" button). */
  isAddingAssignment = false;
  readonly addAssignmentInput = viewChild<ElementRef<HTMLInputElement>>(
    'addAssignmentInput',
  );
  private addResourceSubject$ = new Subject<string>();

  // Staged direct assignment (after resource selected, before confirm)
  stagedResource: ResourceDto | null = null;
  stagedStartDate: Date | null = null;
  stagedEndDate: Date | null = null;

  constructor() {
    // catchError sits on the inner observable, so a failed load becomes the
    // error state without completing the pipeline for later reloads.
    this.assignmentsLoad$
      .pipe(
        switchMap(({ id, silent }) =>
          this.assignmentService.getAssignmentsByMatter(id).pipe(
            map((assignments) => ({ ok: true as const, silent, assignments })),
            catchError((error: unknown) =>
              of({ ok: false as const, silent, error }),
            ),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((outcome) => {
        // A silent refresh replaces only the list the Assignment Notes card
        // reads, and on failure keeps the current one: it is a background
        // refresh, with no loading or error state of its own.
        if (outcome.silent) {
          this.silentRefreshPending = false;
          if (outcome.ok) {
            this.assignments = outcome.assignments;
            // Fresh data, so an earlier failed load's error no longer applies.
            this.assignmentsLoadFailed = false;
          }
          return;
        }
        this.isLoadingAssignments = false;
        if (outcome.ok) {
          this.onAssignmentsLoaded(outcome.assignments);
        } else {
          this.onAssignmentsLoadFailed(outcome.error);
        }
      });
  }

  ngOnInit(): void {
    // Subscribe to role changes to update read-only state reactively
    this.authService.userRoles$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.checkUserPermissions();
    });

    // Also check immediately in case roles are already loaded
    this.checkUserPermissions();

    // Setup add-resource typeahead debouncing
    this.addResourceSubject$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        if (query.trim().length >= 3) {
          this.performResourceSearch(query.trim());
        } else {
          this.resourceSuggestions = [];
          this.resourceNotFound = false;
          this.isSearchingResources = false;
        }
      });

    this.featureFlagService
      .isFeatureFlagEnabled('comparison-pop-ups')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isComparisonPopUpsEnabled = enabled;
      });

    if (this.isDialogMode) {
      this.loadMatter();
    } else {
      this.route.params.subscribe((params) => {
        const id = params['id'];
        if (id) {
          this.clientMatterNumber = id;
          this.loadMatter();
        } else {
          this.error = 'No matter ID provided';
        }
      });
    }
  }

  /**
   * Refresh every permission-derived flag. Called both on userRoles$ emissions and
   * immediately on init — keeping it in one place stops the two call sites from
   * drifting apart when a flag is added.
   */
  private checkUserPermissions(): void {
    this.isNotesReadOnly = !this.authService.canEditMatters();
    this.canCreateAssignments = this.authService.canCreateAssignments();
    this.canDeleteAssignments = this.authService.canDeleteAssignments();
    this.canEditAssignments = this.authService.canEditAssignments();
    this.canReadResourceAssignmentNotes =
      this.authService.canReadResourceAssignmentNotes();
    this.canReadManagerAssignmentNotes =
      this.authService.canReadManagerAssignmentNotes();
    this.canWriteManagerAssignmentNotes =
      this.authService.canWriteManagerAssignmentNotes();
  }

  canDeactivate(): boolean | Observable<boolean> {
    // Check notes component for unsaved changes
    if (this.entityNotes()?.hasUnsavedChanges()) {
      return new Observable<boolean>((observer) => {
        this.entityNotes()!
          .checkUnsavedChangesForNavigation()
          .then((canLeave) => {
            observer.next(canLeave);
            observer.complete();
          });
      });
    }
    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMatter(): void {
    if (!this.clientMatterNumber) return;

    this.isLoading = true;
    this.error = null;

    this.matterService.getMatterDetails(this.clientMatterNumber).subscribe({
      next: (matter) => {
        this.matter = matter;
        if (!this.isDialogMode) {
          const matterIdLabel =
            matter.client?.clientNumber && matter.matterNumber
              ? `#${matter.client.clientNumber}-${matter.matterNumber}`
              : matter.matterName;
          this.breadcrumbService.setDynamicLabel(':matterId', matterIdLabel);
        }
        this.isLoading = false;
        // Load assignments after matter is loaded
        this.loadAssignments();
      },
      error: (error) => {
        this.error =
          describeApiError(error, 'Failed to load matter details');
        this.isLoading = false;
      },
    });
  }

  private loadAssignments(): void {
    if (!this.clientMatterNumber) return;

    this.isLoadingAssignments = true;
    this.assignmentsLoadFailed = false;
    // Supersedes any silent refresh in flight (switchMap drops it).
    this.silentRefreshPending = false;
    this.assignmentsLoad$.next({ id: this.clientMatterNumber, silent: false });
  }

  /**
   * Refreshes only the DTO list the Assignment Notes card reads, for a new
   * assignment the Assignments card already shows. The table, its rows (which
   * the card tracks by identity, so replacing them would close an expanded
   * row) and the loading state are left alone. It runs through the same
   * pipeline as the full loads, so either one supersedes the other; but it
   * would cancel a full load in flight, which then never settles the table, so
   * in that case the full load is issued again instead.
   */
  private refreshAssignmentNotesSilently(): void {
    if (!this.clientMatterNumber) return;
    if (this.isLoadingAssignments) {
      this.loadAssignments();
      return;
    }
    this.silentRefreshPending = true;
    this.assignmentsLoad$.next({ id: this.clientMatterNumber, silent: true });
  }

  private onAssignmentsLoaded(assignments: MatterAssignmentDto[]): void {
    this.assignments = assignments;
    // Ownership is derived by the card from employeeId when the dialog
    // opens — not snapshotted here, where the current user may not be loaded.
    this.assignmentRows = this.assignments.map((a) =>
      toAssignmentRow(a, {
        employeeId: a.employeeId,
        fullName: a.fullName,
        title: a.title,
        dialogTitle: this.matter?.matterName || 'Matter',
        clientMatterNumber: this.clientMatterNumber ?? undefined,
        resourceName: a.fullName || '',
      }),
    );
  }

  private onAssignmentsLoadFailed(error: unknown): void {
    console.error('Failed to load assignments:', error);
    this.assignments = [];
    this.assignmentsLoadFailed = true;
    // assignmentRows is what the card renders, so clearing only the DTOs
    // would leave the previous matter's rows and practice-area chips under
    // this matter's header. Failure path only — a reload after add/remove must
    // not blank the table.
    this.assignmentRows = [];
  }

  retry(): void {
    this.loadMatter();
  }

  goBack(): void {
    this.location.back();
  }

  openInFloatingDialog(): void {
    if (!this.matter || !this.clientMatterNumber) {
      return;
    }
    this.floatingDialogService.open(
      MatterDetailsComponent,
      this.matter.matterName,
      { clientMatterNumber: this.clientMatterNumber },
    );
  }

  /**
   * The assignments card's "View all notes" action. Aligned to the top rather
   * than centred: the card is tall, and centring it would push its header
   * off-screen. Focus moves with it, for keyboard and screen-reader users.
   */
  scrollToAssignmentNotes(): void {
    const section = this.assignmentNotesSection()?.nativeElement;
    if (!section) return;
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    section.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    // Without this, focus stays on the button and the next Tab lands back in
    // the Assignments table, now off-screen. preventScroll keeps focus from
    // cutting the smooth scroll short.
    section.focus({ preventScroll: true });
  }

  getContactName(client: MatterClientDto | undefined): string | null {
    // Contact information is no longer available in the new model
    return null;
  }

  getMatterStatus(): string {
    if (!this.matter?.closeDate) {
      return 'Open';
    }

    const closeDate = new Date(this.matter.closeDate);
    const today = new Date();

    return closeDate > today ? 'Open' : 'Closed';
  }

  getStatusBadgeClass(): string {
    const status = this.getMatterStatus();
    return status === 'Open' ? 'badge-open' : 'badge-closed';
  }

  getMatterBreadcrumbContext(): Breadcrumb[] {
    return this.breadcrumbService.getCurrentBreadcrumbs();
  }

  getDateRangeText(): string {
    if (!this.matter) return '';

    const openDate = this.matter.openDate
      ? new Date(this.matter.openDate)
      : null;
    const closeDate = this.matter.closeDate
      ? new Date(this.matter.closeDate)
      : null;

    if (!openDate && !closeDate) {
      return 'No dates set';
    }

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    };

    if (openDate && closeDate) {
      return `${formatDate(openDate)} - ${formatDate(closeDate)}`;
    } else if (openDate) {
      return `${formatDate(openDate)} - Present`;
    } else if (closeDate) {
      return `Until ${formatDate(closeDate)}`;
    }

    return '';
  }

  getMatterIdDisplay(): string {
    if (!this.matter?.client?.clientNumber || !this.matter?.matterNumber)
      return '';
    return `#${this.matter.client.clientNumber}-${this.matter.matterNumber}`;
  }

  getMatterIdForCopy(): string {
    if (!this.matter?.client?.clientNumber || !this.matter?.matterNumber)
      return '';
    return `${this.matter.client.clientNumber}-${this.matter.matterNumber}`;
  }

  // Assignment methods
  navigateToRequest(requestId: number): void {
    const currentCrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
    this.router.navigate(['/requests', requestId], {
      state: {
        breadcrumbOverride: [
          ...currentCrumbs,
          { label: ':requestId', url: '' },
        ],
      },
    });
  }

  navigateToResource(employeeId: string): void {
    const currentCrumbs = this.breadcrumbService.getCurrentBreadcrumbs();
    this.router.navigate(['/resources', employeeId], {
      state: {
        breadcrumbOverride: [
          ...currentCrumbs,
          { label: ':employeeId', url: '' },
        ],
      },
    });
  }

  /**
   * Handle notes saved event
   */
  onNotesSaved(notesWithMentions: NotesWithMentionsDto): void {
    if (this.matter) {
      // Update the local matter object with new notes data
      this.matter.notesInfo = notesWithMentions;
    }
  }

  /**
   * Handle notes edit mode request
   */
  onNotesEditModeRequested(): void {
    // Enter edit mode on the notes component
    this.entityNotes()?.enterEditMode();
  }

  // ---------------------------------------------------------------------------
  // Direct assignment management (bypass the Request workflow)
  // ---------------------------------------------------------------------------

  startAddAssignment(): void {
    this.isAddingAssignment = true;
    // The input is inside an @if, so focus after the next change-detection pass.
    setTimeout(() => this.addAssignmentInput()?.nativeElement.focus());
  }

  cancelAddAssignment(): void {
    if (this.isCreatingAssignment) return;
    this.onCancelStagedAssignment();
    this.clearAddResourceTypeahead();
    this.isAddingAssignment = false;
  }

  onAddResourceInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.addResourceQuery = value;
    this.isSearchingResources = value.trim().length >= 3;
    this.resourceNotFound = false;
    this.addResourceSubject$.next(value);
  }

  clearAddResourceTypeahead(): void {
    this.addResourceQuery = '';
    this.resourceSuggestions = [];
    this.resourceNotFound = false;
    this.isSearchingResources = false;
    // Reset distinctUntilChanged so the same query can be re-issued
    this.addResourceSubject$.next('');
  }

  onAddResourceBlur(): void {
    // Delay so click events on suggestions can fire first
    setTimeout(() => {
      this.resourceSuggestions = [];
      this.resourceNotFound = false;
    }, 200);
  }

  onSelectResourceToAssign(resource: ResourceDto): void {
    if (!this.clientMatterNumber || this.isCreatingAssignment) return;
    this.stagedResource = resource;
    this.stagedStartDate = null;
    this.stagedEndDate = null;
    this.resourceSuggestions = [];
    this.resourceNotFound = false;
  }

  onConfirmAssignment(): void {
    if (
      !this.stagedResource ||
      !this.clientMatterNumber ||
      this.isCreatingAssignment
    )
      return;

    this.isCreatingAssignment = true;
    const resource = this.stagedResource;
    this.assignmentService
      .createDirectAssignment(
        this.clientMatterNumber,
        resource.employeeId,
        this.stagedStartDate ? this.stagedStartDate.toISOString() : null,
        this.stagedEndDate ? this.stagedEndDate.toISOString() : null,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // No status is sent, so the API creates the row Pending: the resource is not
          // told until someone marks it Active, and the message has to say so.
          this.snackbarService.showSuccess(
            `${resource.fullName} has been assigned to this matter as Pending. Mark the assignment Active to confirm it and notify them.`,
          );
          this.stagedResource = null;
          this.stagedStartDate = null;
          this.stagedEndDate = null;
          this.clearAddResourceTypeahead();
          this.isCreatingAssignment = false;
          this.isAddingAssignment = false;
          this.loadAssignments();
        },
        error: (error) => {
          this.snackbarService.showError(
            describeApiError(error, 'Failed to assign resource. Please try again.'),
          );
          this.isCreatingAssignment = false;
        },
      });
  }

  onCancelStagedAssignment(): void {
    this.stagedResource = null;
    this.stagedStartDate = null;
    this.stagedEndDate = null;
    this.addResourceQuery = '';
  }

  onRemoveAssignment(row: AssignmentRow): void {
    if (!row.assignedResourceId) return;

    const resourceLabel = row.fullName || 'this resource';
    const dialogRef = this.dialog.open<
      ConfirmationDialogComponent,
      ConfirmationDialogData,
      boolean
    >(ConfirmationDialogComponent, {
      width: '420px',
      autoFocus: false,
      data: {
        title: 'Remove Assignment',
        message: `Are you sure you want to remove ${resourceLabel} from this matter?`,
        confirmText: 'Remove',
        cancelText: 'Cancel',
        icon: 'warning',
        iconColor: '#f59e0b',
        highlightColor: '#fef3c7',
        confirmColor: 'var(--dark-red-error)',
        cancelVariant: 'destructive',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.assignmentService
        .deleteAssignment(row.assignedResourceId!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.snackbarService.showSuccess(
              `Removed ${resourceLabel} from this matter.`,
            );
            this.loadAssignments();
          },
          error: (error) => {
            this.snackbarService.showError(
              describeApiError(error, 'Failed to remove assignment. Please try again.'),
            );
          },
        });
    });
  }

  /** Called when the assignments card emits a dialog-driven delete (already executed). */
  loadAssignmentsAfterRemove(_row: AssignmentRow): void {
    this.snackbarService.showSuccess('Assignment removed.');
    this.loadAssignments();
  }

  /**
   * Called when the assignments card patches a row in place via the edit dialog.
   * The row needs no reload, but the Assignment Notes card renders the DTOs, so
   * the edited notes and assignment type are carried back onto a new copy of
   * the list for it.
   *
   * A row the list does not hold is one the card created from a synthetic
   * latest-billed row ("Mark Active"): the page has never fetched it, so there
   * is nothing to patch, and the list is refreshed silently to pick it up.
   */
  onAssignmentChanged(row: AssignmentRow): void {
    const updated = applyAssignmentRowEdits(this.assignments, row);
    if (updated === this.assignments) {
      this.refreshAssignmentNotesSilently();
      return;
    }
    this.assignments = updated;
    // A load sent before this edit, silent or full, would land the pre-edit
    // note over it. The server already holds the edit, so a fresh request
    // includes it, and switchMap drops the older one. A full load in flight is
    // re-issued as a full load (see refreshAssignmentNotesSilently).
    if (this.silentRefreshPending || this.isLoadingAssignments) {
      this.refreshAssignmentNotesSilently();
    }
  }

  private performResourceSearch(query: string): void {
    this.resourceService
      .getResources({
        page: 1,
        pageSize: 10,
        search: query,
        sortBy: 'name',
        sortDirection: 'asc',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const assignedIds = new Set(
            this.assignmentRows
              .map((row) => row.employeeId)
              .filter((id): id is string => !!id),
          );
          this.resourceSuggestions = (result.items || []).filter(
            (r) => r.employeeId && !assignedIds.has(r.employeeId),
          );
          this.resourceNotFound = this.resourceSuggestions.length === 0;
          this.isSearchingResources = false;
        },
        error: () => {
          this.resourceSuggestions = [];
          this.resourceNotFound = true;
          this.isSearchingResources = false;
        },
      });
  }
}
